/**
 * Autoplay tour script.
 *
 * One entry per scene in REFERENCE_SCENES order. Each beat spotlights a
 * hotspot id (from reference/hotspots.ts) and, where the change is visible
 * through the insets, applies a state patch; every beat carries a short
 * bilingual caption for the tour bar. Beats are sorted by `at`; the last beat
 * of a scene points at the control that leads on, so the scene change the
 * engine performs at the end of the dwell reads as a consequence, not a cut.
 *
 * Tests assert: every scene present once, in order; beats sorted; every
 * hotspot id exists for its scene; durations in 6000..14000.
 */

import type { TourScript } from '../types';

export const TOUR_SCRIPT: TourScript = [
  {
    scene: 'classroom',
    duration: 9000,
    beats: [
      {
        at: 600,
        hotspot: 'learner-8',
        patch: { selectedStudent: 8 },
        caption: { ru: 'Красный сигнал Кирилла: пробел в теме «Законы Ньютона»', en: 'Kirill’s red signal: a gap in Newton’s laws' },
      },
      {
        at: 4200,
        hotspot: 'learner-2',
        patch: { selectedStudent: 2 },
        caption: { ru: 'Жёлтый сигнал Богдана — другая причина, другое действие', en: 'Bogdan’s amber signal: a different cause, a different action' },
      },
      {
        at: 7200,
        hotspot: 'class-action',
        patch: { selectedStudent: 8 },
        caption: { ru: 'Учитель применяет решение и идёт к причине', en: 'The teacher applies the decision and moves on to the cause' },
      },
    ],
  },
  {
    scene: 'motivation',
    duration: 10000,
    beats: [
      {
        at: 600,
        hotspot: 'cause-0',
        patch: { causeStep: 0 },
        caption: { ru: 'Звено 1: пропущена концепция', en: 'Link 1: a missed concept' },
      },
      {
        at: 4000,
        hotspot: 'cause-3',
        patch: { causeStep: 3 },
        caption: { ru: 'Звено 4: усилия без результата', en: 'Link 4: effort without a visible result' },
      },
      {
        at: 7400,
        hotspot: 'motivation-intervention',
        caption: { ru: 'Вмешательство разрывает замкнутый круг', en: 'The intervention breaks the loop' },
      },
    ],
  },
  {
    scene: 'teacher-goal',
    duration: 10000,
    beats: [
      {
        at: 600,
        hotspot: 'goal-0',
        patch: { teacherGoal: 0 },
        caption: { ru: 'Цель «Понятие и смысл»: маршрут построен', en: 'Goal: Meaning and understanding. The route is set' },
      },
      {
        at: 4200,
        hotspot: 'goal-1',
        patch: { teacherGoal: 1 },
        caption: { ru: 'Смена цели на «Применение на практике» сдвигает четыре оси', en: 'Switching to Apply in practice moves all four axes' },
      },
      {
        at: 7600,
        hotspot: 'axis-3-1',
        caption: { ru: 'Паттерн рекомендаций: наводящие вопросы', en: 'Hint pattern: guiding questions' },
      },
    ],
  },
  {
    scene: 'knowledge-map',
    duration: 11000,
    beats: [
      {
        at: 600,
        hotspot: 'node-quadratic-equations',
        patch: { selectedGraphNode: 'quadratic-equations', graphDrawerOpen: true },
        caption: { ru: 'В центре карты — квадратные уравнения', en: 'Quadratic equations at the centre of the map' },
      },
      {
        at: 4400,
        hotspot: 'node-vieta',
        patch: { selectedGraphNode: 'vieta', graphDrawerOpen: true },
        caption: { ru: 'Теорема Виета — слабый узел', en: 'Vieta’s theorem: a weak node' },
      },
      {
        at: 8000,
        hotspot: 'map-cta',
        caption: { ru: '«Подобрать задания» — граф ведёт в маршрут', en: 'Select exercises: the graph feeds the route' },
      },
    ],
  },
  {
    scene: 'personal-route',
    duration: 11000,
    beats: [
      {
        at: 600,
        hotspot: 'route-next',
        caption: { ru: 'Следующий шаг Алексея — квадратные уравнения', en: 'Alexey’s next step: quadratic equations' },
      },
      {
        at: 4200,
        hotspot: 'profile-time-1',
        patch: { profile: { time: 1 } },
        caption: { ru: 'Меньше доступного времени — шаг пересчитан', en: 'Less available time, so the step is recomputed' },
      },
      {
        at: 7800,
        hotspot: 'route-compare',
        patch: { compareRoute: true },
        caption: { ru: 'Сравнение с Марией: у неё свой маршрут', en: 'Compared with Maria, who has her own route' },
      },
    ],
  },
  {
    scene: 'learning-cycle',
    duration: 10000,
    beats: [
      {
        at: 600,
        hotspot: 'cycle-2',
        patch: { cycleStep: 2 },
        caption: { ru: 'Шаг 3: следующий доступный шаг', en: 'Step 3: the next available step' },
      },
      {
        at: 3800,
        hotspot: 'cycle-4',
        patch: { cycleStep: 4 },
        caption: { ru: 'Шаг 5: анализ ответа — что получилось, а что нет', en: 'Step 5: response analysis, what worked and what did not' },
      },
      {
        at: 7000,
        hotspot: 'cycle-6',
        patch: { cycleStep: 6 },
        caption: { ru: 'Шаг 7: сигнал учителю — данные для решения', en: 'Step 7: the teacher signal, data for the decision' },
      },
    ],
  },
  {
    scene: 'agency',
    duration: 9000,
    beats: [
      {
        at: 600,
        hotspot: 'agency-mastery',
        patch: { rewardMode: 'mastery' },
        caption: { ru: 'Награда за освоение: рост, который чувствуется', en: 'Reward for mastery: growth you can feel' },
      },
      {
        at: 3800,
        hotspot: 'agency-action',
        patch: { rewardMode: 'action' },
        caption: { ru: 'Награда за действие: пустые баллы', en: 'Reward for action: empty points' },
      },
      {
        at: 6600,
        hotspot: 'agency-support',
        patch: { rewardMode: 'mastery' },
        caption: { ru: 'Поддержка вовремя: подсказка, когда застрял', en: 'Timely support: a hint when you are stuck' },
      },
    ],
  },
  {
    scene: 'teacher-workspace',
    duration: 11000,
    beats: [
      {
        at: 600,
        hotspot: 'workspace-gap',
        patch: { teacherAction: 1 },
        caption: { ru: 'Вероятный пробел: связь переменных', en: 'Probable gap: connecting variables' },
      },
      {
        at: 4200,
        hotspot: 'teacher-action-2',
        patch: { teacherAction: 2 },
        caption: { ru: 'Другое действие — Practice Set — меняет маршрут справа', en: 'A different action, Practice Set, changes the route on the right' },
      },
      {
        at: 7800,
        hotspot: 'workspace-apply',
        patch: { teacherAction: 1 },
        caption: { ru: 'Guided Exploration применено: маршрут обновлён', en: 'Guided Exploration applied: the route is updated' },
      },
    ],
  },
  {
    scene: 'technology',
    duration: 10000,
    beats: [
      {
        at: 600,
        hotspot: 'depth-teacher',
        patch: { explanationDepth: 'teacher' },
        caption: { ru: 'Глубина «Учитель»: объяснение за 20 секунд', en: 'Teacher depth: a twenty second explanation' },
      },
      {
        at: 3800,
        hotspot: 'depth-method',
        patch: { explanationDepth: 'methodologist' },
        caption: { ru: 'Глубина «Методист»: глубже о механизмах', en: 'Methodologist depth: deeper into the mechanisms' },
      },
      {
        at: 7000,
        hotspot: 'research-magicbrain',
        caption: { ru: 'Научные прототипы отделены: не входят в продукт сегодня', en: 'Research prototypes stay separate: not in the product today' },
      },
    ],
  },
  {
    scene: 'finale',
    duration: 9000,
    beats: [
      {
        at: 600,
        hotspot: 'finale-teacher',
        caption: { ru: 'Учитель решает', en: 'The teacher decides' },
      },
      {
        at: 3200,
        hotspot: 'finale-student',
        caption: { ru: 'Ученик движется', en: 'The learner moves' },
      },
      {
        at: 5800,
        hotspot: 'finale-prompt-1',
        caption: { ru: 'Спросите о сцене, ученике или маршруте', en: 'Ask about a scene, a learner or a route' },
      },
    ],
  },
];
